import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { blogArticles, authors } from '@/mocks/blog';
import { useScrollReveal } from '@/hooks/useScrollReveal';

function BlogDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState<string[]>([]);
  const [commentPosted, setCommentPosted] = useState(false);

  const article = blogArticles.find((a) => a.slug === slug);
  const author = article ? authors.find((a) => a.slug === article.authorSlug) : null;

  // Related articles computed before any conditional rendering
  const relatedByCategory = article
    ? blogArticles.filter((a) => a.slug !== article.slug && a.category === article.category).slice(0, 3)
    : [];
  const related = article
    ? (relatedByCategory.length >= 3
        ? relatedByCategory
        : [...relatedByCategory, ...blogArticles.filter((a) => a.slug !== article.slug && a.category !== article.category)].slice(0, 3))
    : [];

  const { ref: authorRef, isVisible: authorVisible } = useScrollReveal(0.1);
  const { ref: relatedRef, isVisible: relatedVisible } = useScrollReveal(0.1);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4">
        <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 mb-6">
          <i className="ri-article-line text-3xl text-foreground-400" />
        </div>
        <h1 className="font-heading font-bold text-2xl text-foreground-50 mb-2">Article introuvable</h1>
        <p className="text-foreground-400 mb-6 text-center">Cet article n'existe pas ou a été supprimé.</p>
        <button
          onClick={() => navigate('/blog')}
          className="px-6 py-3 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors whitespace-nowrap"
        >
          Retour au blog
        </button>
      </div>
    );
  }

  const shareUrl = encodeURIComponent(`https://worldapeiron.com/blog/${article.slug}`);
  const shareTitle = encodeURIComponent(article.title);

  const handleComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (commentText.trim()) {
      setComments((prev) => [...prev, commentText.trim()]);
      setCommentText('');
      setCommentPosted(true);
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20 md:pt-24">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-8 lg:px-12 py-4">
          <nav className="flex items-center gap-2 text-sm text-foreground-400" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-400 transition-colors">Accueil</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/blog" className="hover:text-primary-400 transition-colors">Blog</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-400 truncate">{article.category}</span>
          </nav>
        </div>

        {/* Header */}
        <section className="w-full px-4 md:px-8 lg:px-12 mb-8">
          <div className="max-w-3xl mx-auto">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-primary-500/10 text-primary-400 border border-primary-400/30 mb-4">
              {article.category}
            </span>
            <h1 className="font-heading font-bold text-2xl md:text-4xl lg:text-5xl text-foreground-50 mb-6 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 mb-6">
              <Link to={`/author/${article.authorSlug}`} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                {article.authorImage ? (
                  <img src={article.authorImage} alt={article.author} className="w-11 h-11 rounded-full object-cover" />
                ) : (
                  <div className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-500/10">
                    <span className="text-sm font-semibold text-primary-400">{article.author.charAt(0)}</span>
                  </div>
                )}
                <div>
                  <span className="block text-sm font-medium text-foreground-400">{article.author}</span>
                  <span className="block text-xs text-foreground-500">{article.authorRole}</span>
                </div>
              </Link>
              <div className="ml-auto flex items-center gap-3 text-xs text-foreground-400 flex-wrap">
                <span>{article.publishedAt}</span>
                <span>·</span>
                <span>{article.readTime} min de lecture</span>
                <span>·</span>
                <span>{article.views.toLocaleString('fr-FR')} vues</span>
              </div>
            </div>
          </div>
        </section>

        {/* Cover */}
        <section className="w-full px-4 md:px-8 lg:px-12 mb-10">
          <div className="max-w-4xl mx-auto rounded-2xl overflow-hidden h-64 md:h-[420px]">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover"
              title={article.title}
            />
          </div>
        </section>

        {/* Body */}
        <section className="w-full px-4 md:px-8 lg:px-12 pb-10">
          <div className="max-w-3xl mx-auto">
            <article className="space-y-5">
              {article.content.map((paragraph, idx) => (
                <p key={idx} className="text-base md:text-lg text-foreground-400 leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </article>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mt-8">
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1.5 rounded-full bg-background-100 border border-background-200/70 text-xs text-foreground-500">
                  #{tag}
                </span>
              ))}
            </div>

            {/* Share */}
            <div className="flex items-center gap-3 mt-8 pt-6 border-t border-background-200/70">
              <span className="text-sm text-foreground-400">Partager :</span>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 hover:text-primary-400 transition-colors"
                aria-label="Partager sur Facebook"
              >
                <i className="ri-facebook-line text-lg" />
              </a>
              <a
                href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareTitle}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 hover:text-primary-400 transition-colors"
                aria-label="Partager sur X"
              >
                <i className="ri-twitter-x-line text-lg" />
              </a>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 hover:text-primary-400 transition-colors"
                aria-label="Partager sur LinkedIn"
              >
                <i className="ri-linkedin-line text-lg" />
              </a>
              <a
                href={`https://wa.me/?text=${shareTitle}%20${shareUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 hover:text-primary-400 transition-colors"
                aria-label="Partager sur WhatsApp"
              >
                <i className="ri-whatsapp-line text-lg" />
              </a>
            </div>
          </div>
        </section>

        {/* Author Card */}
        <section ref={authorRef} className="w-full px-4 md:px-8 lg:px-12 pb-12">
          <div className="max-w-3xl mx-auto">
            <div className={`p-5 md:p-6 rounded-2xl bg-background-100 border border-background-200/70 transition-all duration-1000 ${authorVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <div className="flex flex-col sm:flex-row items-start gap-4">
                {author?.image ? (
                  <img src={author.image} alt={author.name} className="w-16 h-16 rounded-xl object-cover shrink-0" />
                ) : (
                  <div className="w-16 h-16 flex items-center justify-center rounded-xl bg-primary-500/10 shrink-0">
                    <span className="text-xl font-semibold text-primary-400">{article.author.charAt(0)}</span>
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="font-heading font-bold text-lg text-foreground-50 mb-1">{author?.name || article.author}</h3>
                  <p className="text-sm text-primary-400 mb-2">{author?.role || article.authorRole}</p>
                  <p className="text-sm text-foreground-400 leading-relaxed mb-3">
                    {author?.bio || article.authorBio}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to={`/author/${article.authorSlug}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-500/10 text-primary-400 text-xs font-medium border border-primary-400/20 hover:bg-primary-500/20 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-user-line" />
                      Voir la fiche auteur
                    </Link>
                    <Link
                      to="/about"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary-500/10 text-secondary-400 text-xs font-medium border border-secondary-400/20 hover:bg-secondary-500/20 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-information-line" />
                      À propos
                    </Link>
                    {author?.socials.twitter && (
                      <a
                        href={author.socials.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-background-50 border border-background-200/70 text-foreground-400 hover:text-primary-400 transition-colors"
                        aria-label="Twitter"
                      >
                        <i className="ri-twitter-x-line" />
                      </a>
                    )}
                    {author?.socials.linkedin && (
                      <a
                        href={author.socials.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-background-50 border border-background-200/70 text-foreground-400 hover:text-primary-400 transition-colors"
                        aria-label="LinkedIn"
                      >
                        <i className="ri-linkedin-line" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Comments */}
        <section className="w-full px-4 md:px-8 lg:px-12 pb-12">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading font-bold text-xl text-foreground-50 mb-6">
              Commentaires ({article.comments.length + comments.length})
            </h2>

            <div className="space-y-4 mb-8">
              {article.comments.map((comment) => (
                <div key={comment.id} className="flex gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/10 shrink-0">
                    <span className="text-sm font-semibold text-primary-400">{comment.author.charAt(0)}</span>
                  </div>
                  <div className="bg-background-100 border border-background-200/70 rounded-lg p-4 flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-foreground-400">{comment.author}</span>
                      <span className="text-xs text-foreground-500">{comment.date}</span>
                    </div>
                    <p className="text-sm text-foreground-500">{comment.content}</p>
                  </div>
                </div>
              ))}
              {comments.map((c, idx) => (
                <div key={`new-${idx}`} className="flex gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-500/10 shrink-0">
                    <span className="text-sm font-semibold text-secondary-300">V</span>
                  </div>
                  <div className="bg-background-100 border border-background-200/70 rounded-lg p-4 flex-1">
                    <span className="text-sm font-medium text-foreground-400">Vous</span>
                    <p className="text-sm text-foreground-500 mt-1">{c}</p>
                  </div>
                </div>
              ))}
            </div>

            <form onSubmit={handleComment} className="space-y-3">
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="Ajouter un commentaire..."
                className="w-full bg-background-100 border border-background-300/60 rounded-lg px-4 py-3 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-primary-400 resize-none"
              />
              {commentPosted && (
                <p className="text-sm text-secondary-300 flex items-center gap-2">
                  <i className="ri-check-line" />
                  Merci pour votre commentaire !
                </p>
              )}
              <button
                type="submit"
                disabled={!commentText.trim()}
                className="px-6 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-md hover:bg-primary-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
              >
                Publier
              </button>
            </form>
          </div>
        </section>

        {/* Related */}
        <section ref={relatedRef} className="w-full px-4 md:px-8 lg:px-12 pb-20">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-50 mb-2">
              Articles similaires
            </h2>
            <p className="text-sm text-foreground-400 mb-6">
              Dans la catégorie « {article.category} » et plus
            </p>
            <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 transition-all duration-1000 ${relatedVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              {related.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/blog/${rel.slug}`}
                  className="group rounded-xl overflow-hidden bg-background-100 border border-background-200/70 hover:border-primary-400/40 transition-all duration-300"
                >
                  <div className="h-40 w-full overflow-hidden">
                    <img src={rel.coverImage} alt={rel.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-4">
                    <span className="text-xs text-primary-400">{rel.category}</span>
                    <h3 className="font-heading font-semibold text-sm text-foreground-50 mt-1 line-clamp-2">{rel.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default BlogDetailPage;